import React, { useState } from "react";
import {
    type Cell,
    type CellTemplate,
    type Compatible,
    type UncertainCompatible,
} from "@silevis/reactgrid";
import { Select, MenuItem, Chip, Box, FormControl } from "@mui/material";

export interface DropdownGridCell extends Cell {
    type: "dropdown";
    value: string;
    options: {
        value: string;
        label: string;
        color?: any;
    }[];
    variant?: "default" | "chip";
}

/**
 * DropdownCellTemplate - "Always Interactive" Version
 * Bypasses ReactGrid's Selection/Edit lifecycle to provide instant 1-click interaction.
 */
export class DropdownCellTemplate implements CellTemplate<DropdownGridCell> {
    getCompatibleCell(
        uncertainCell: UncertainCompatible<DropdownGridCell>,
    ): Compatible<DropdownGridCell> {
        const value = uncertainCell.value || "";
        const options = uncertainCell.options || [];
        const variant = uncertainCell.variant || "default";

        return {
            ...uncertainCell,
            type: "dropdown",
            value: value,
            text: value,
            options,
            variant,
        };
    }

    handleKeyDown(
        cell: Compatible<DropdownGridCell>,
        keyCode: number,
        _ctrl: boolean,
        _shift: boolean,
        _alt: boolean,
        _key?: string,
        _capsLock?: boolean,
    ): { cell: Compatible<DropdownGridCell>; enableEditMode: boolean } {
        // Enter or Space starts editing (standard accessibility)
        if (keyCode === 13 || keyCode === 32) {
            return { cell, enableEditMode: true };
        }
        return { cell, enableEditMode: false };
    }

    update(
        cell: Compatible<DropdownGridCell>,
        cellToMerge: UncertainCompatible<DropdownGridCell>,
    ): Compatible<DropdownGridCell> {
        return this.getCompatibleCell({ ...cell, ...cellToMerge });
    }

    render(
        cell: Compatible<DropdownGridCell>,
        isInEditMode: boolean,
        onCellChanged: (
            cell: Compatible<DropdownGridCell>,
            commit: boolean,
        ) => void,
    ): React.ReactNode {
        return (
            <InteractiveDropdown
                cell={cell}
                isInEditMode={isInEditMode}
                onCellChanged={onCellChanged}
            />
        );
    }
}

/**
 * Internal Component to manage local 'open' state and stop propagation.
 */
const InteractiveDropdown: React.FC<{
    cell: Compatible<DropdownGridCell>;
    isInEditMode: boolean;
    onCellChanged: (
        cell: Compatible<DropdownGridCell>,
        commit: boolean,
    ) => void;
}> = ({ cell, isInEditMode, onCellChanged }) => {
    const [menuOpen, setMenuOpen] = useState(false);

    // If the current value isn't among the options (e.g. legacy data with
    // "Percentage"), append it so MUI Select doesn't warn about out-of-range.
    const effectiveOptions = React.useMemo(() => {
        if (cell.value && !cell.options.some((o) => o.value === cell.value)) {
            return [...cell.options, { value: cell.value, label: cell.value }];
        }
        return cell.options;
    }, [cell.value, cell.options]);

    // Open immediately if ReactGrid enters edit mode (Enter/Space)
    const showSelect = menuOpen || isInEditMode;

    // ── Read mode: lightweight display ─────────────────────────────────
    if (!showSelect) {
        const opt = effectiveOptions.find((o) => o.value === cell.value);
        const displayLabel = opt?.label || cell.value || "";

        // Single click opens the dropdown menu
        const handleClick = (e: React.PointerEvent) => {
            e.stopPropagation();
            setMenuOpen(true);
        };

        if (cell.variant === "chip" && opt) {
            return (
                <Box
                    onPointerDown={handleClick}
                    sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 0.5,
                        cursor: "pointer",
                    }}
                >
                    <Chip
                        label={opt.label}
                        size="small"
                        color={opt.color || "default"}
                        sx={{
                            height: "20px",
                            fontSize: "11px",
                            cursor: "pointer",
                            pointerEvents: "none",
                        }}
                    />
                </Box>
            );
        }

        return (
            <span
                onPointerDown={handleClick}
                style={{
                    width: "100%",
                    display: "block",
                    padding: "0 8px",
                    fontSize: "12px",
                    cursor: "pointer",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {displayLabel}
            </span>
        );
    }

    // ── Menu open: full MUI Select ─────────────────────────────────────
    return (
        <DropdownEditor
            cell={cell}
            effectiveOptions={effectiveOptions}
            onCellChanged={onCellChanged}
            onClose={() => setMenuOpen(false)}
        />
    );
};

/** Only mounted when the menu should be visible. */
const DropdownEditor: React.FC<{
    cell: Compatible<DropdownGridCell>;
    effectiveOptions: { value: string; label: string; color?: any }[];
    onCellChanged: (
        cell: Compatible<DropdownGridCell>,
        commit: boolean,
    ) => void;
    onClose: () => void;
}> = ({ cell, effectiveOptions, onCellChanged, onClose }) => {
    const [open, setOpen] = useState(true); // Start open immediately

    return (
        <Box
            sx={{
                height: "100%",
                width: "100%",
                display: "flex",
                alignItems: "center",
                px: 0.5,
            }}
        >
            <FormControl fullWidth size="small" variant="standard">
                <Select<string>
                    value={cell.value}
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={() => {
                        setOpen(false);
                        onClose();

                        // FOCUS RESCUE: When the MUI popup unmounts, focus often drops
                        // back to the document.body, breaking ReactGrid's keyboard navigation.
                        setTimeout(() => {
                            const active = document.activeElement;
                            if (!active || active === document.body) {
                                const hiddenInput = document.querySelector(
                                    ".rg-hidden-element",
                                ) as HTMLElement | null;
                                if (hiddenInput) {
                                    hiddenInput.focus({ preventScroll: true });
                                }
                            }
                        }, 50);
                    }}
                    autoFocus
                    onChange={(e) => {
                        const newCell = {
                            ...(cell as any),
                            value: e.target.value as string,
                        };
                        onCellChanged(newCell, true);
                    }}
                    onKeyDown={(e) => {
                        // While the menu is open, keep keyboard events contained.
                        if (open) {
                            e.stopPropagation();
                        }
                    }}
                    sx={{
                        fontSize: "12px",
                        "& .MuiSelect-select": {
                            padding: "2px 4px",
                            display: "flex",
                            alignItems: "center",
                        },
                        "&:before, &:after": { display: "none" },
                    }}
                    renderValue={(selected) => {
                        const opt = effectiveOptions.find(
                            (o) => o.value === selected,
                        );
                        if (cell.variant === "chip" && opt) {
                            return (
                                <Chip
                                    label={opt.label}
                                    size="small"
                                    color={opt.color || "default"}
                                    sx={{
                                        height: "20px",
                                        fontSize: "11px",
                                        cursor: "pointer",
                                    }}
                                />
                            );
                        }
                        return (
                            <span style={{ cursor: "pointer" }}>
                                {opt?.label || selected}
                            </span>
                        );
                    }}
                    MenuProps={{
                        disablePortal: true,
                        disableRestoreFocus: true,
                        onKeyDown: (e) => {
                            e.stopPropagation();
                        },
                        sx: {
                            "& .MuiPaper-root": {
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                border: "1px solid #ddd",
                                marginTop: "4px",
                            },
                        },
                    }}
                >
                    {effectiveOptions.map((opt) => (
                        <MenuItem
                            key={opt.value}
                            value={opt.value}
                            sx={{ fontSize: "12px" }}
                        >
                            {cell.variant === "chip" ? (
                                <Chip
                                    label={opt.label}
                                    size="small"
                                    color={opt.color || "default"}
                                    sx={{ height: "20px", fontSize: "11px" }}
                                />
                            ) : (
                                opt.label
                            )}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    );
};
